import { useListSearchState } from "common/hooks";
import { useFetchQuery } from "common/queries/hooks";

import { Paths } from "client/routes";
import { useProductSearchQuery } from "client/products/hooks";
import { SelectedProductFilters } from "client/products/types";
import { EMPTY_PRODUCT_SELECTION, getProductFiltersFromSearch } from "client/products/utils";

import { StorefrontCollection, StorefrontCollectionKind } from "../types";
import { SEARCH_DEFAULTS } from "../utils";
import { keys, urls } from "./constants";

export const useStorefrontCollectionQuery = (
  slug: string,
  kind: StorefrontCollectionKind,
  { enabled = true }: { enabled?: boolean } = {},
) =>
  useFetchQuery<StorefrontCollection>(keys.detail(slug, kind), {
    fetch: { url: urls.storefrontCollection(slug, kind) },
    config: { enabled: enabled && !!slug },
  });

export const useCollectionSearch = (collection: StorefrontCollection) => {
  const [search, updateSearch] =
    useListSearchState<Paths["productCollections"]["detail"]>(SEARCH_DEFAULTS);

  const selectedFilters = collection.show_filters
    ? getProductFiltersFromSearch(search)
    : EMPTY_PRODUCT_SELECTION;

  const {
    data: products,
    isLoading,
    isRefetching,
    error,
  } = useProductSearchQuery({
    q: "",
    page: Number(search.page) || 1,
    page_size: search.page_size,
    sort: search.sort,
    collection: collection.slug,
    ...selectedFilters,
  });

  return {
    products,
    isLoading,
    isRefetching,
    error,
    selectedFilters,
    applyFilters: (next: Partial<SelectedProductFilters>) => updateSearch({ ...next, page: 1 }),
    goToPage: (nextPage: number) => {
      updateSearch({ page: nextPage });
      // Matches ProductSearchPage; without it page 2 opens scrolled to the bottom.
      window.scrollTo(0, 0);
    },
  };
};
